import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EstateStatusItemState } from '../../generated/graphql';
import { ElementsButton, ListItem } from '../../UI';
import type { EstateStatusReadOnlyChecklistItem } from '../types';
import { formatEstateStatusWeight } from '../util';

export interface EstateStatusChecklistItemCardProps {
  item: EstateStatusReadOnlyChecklistItem;
  headerActionLabel?: string;
  onHeaderAction?: () => void;
  topContent?: ReactNode;
  children?: ReactNode;
}

export function EstateStatusChecklistItemCard({
  item,
  headerActionLabel,
  onHeaderAction,
  topContent,
  children,
}: EstateStatusChecklistItemCardProps) {
  const { t } = useTranslation('estateStatus');
  const contributionLabel =
    item.state === EstateStatusItemState.Na
      ? t('item-contribution-excluded')
      : item.state === EstateStatusItemState.Yes
      ? t('item-contribution-earned', { weight: formatEstateStatusWeight(item.weight) })
      : t('item-contribution-not-earned', { weight: formatEstateStatusWeight(item.weight) });
  const headerAction =
    headerActionLabel && onHeaderAction ? (
      <ElementsButton
        type="button"
        theme="text"
        size="small"
        className="!px-3"
        onClick={onHeaderAction}
        label={headerActionLabel}
      />
    ) : null;

  return (
    <ListItem className="EstateStatusChecklistItemCard !block rounded-3xl border border-darkMode-gray-medium/30 !bg-darkMode-gray-darkest/95 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[18px] font-semibold leading-6 text-white">{item.label}</p>
            <span className="rounded-full bg-darkMode-gray-medium/20 px-2.5 py-1 text-xs font-medium uppercase tracking-widest text-white/85">
              {formatEstateStatusWeight(item.weight)}
            </span>
          </div>
          <p className="mt-2 text-sm text-darkMode-gray-light/75">{contributionLabel}</p>
        </div>

        {headerAction ? <div className="flex flex-wrap items-start justify-end gap-3">{headerAction}</div> : null}
      </div>

      {topContent ? <div className="mt-4">{topContent}</div> : null}
      {children ? <div className="mt-4">{children}</div> : null}
    </ListItem>
  );
}
